import { useCallback, useEffect, useMemo, useState } from "react";
import readXlsxFile from "read-excel-file";
import axios from "axios";
import { parseExcelMatrix } from "../../../lib/rdvBulkInput/parseExcelRows";
import {
  buildRdvMap,
  countValidation,
  validateRows,
} from "../../../lib/rdvBulkInput/validateRows";
import { reindexRows, updateRowField } from "../../../lib/rdvBulkInput/rowUtils";
import { generateRandomRdvRows } from "../../../lib/rdvBulkInput/generateRandomRows";
import { RANDOM_ROW_DEFAULT_COUNT } from "../../../lib/rdvBulkInput/constants";
import { createChatSocket } from "../../../utils/chatSocket";

const socket = createChatSocket();

export default function useRdvBulkInputState(apiAddr) {
  const [rows, setRows] = useState([]);
  const [uploadedFileName, setUploadedFileName] = useState("");
  const [validatedAt, setValidatedAt] = useState(null);
  const [isValidating, setIsValidating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveProgress, setSaveProgress] = useState({ done: 0, total: 0 });
  const [dsRdvs, setDsRdvs] = useState([]);
  const [dsSites, setDsSites] = useState([]);
  const [defaultWorkplace, setDefaultWorkplace] = useState("");

  const rdvMap = useMemo(
    () => buildRdvMap(dsRdvs, dsSites),
    [dsRdvs, dsSites],
  );

  const defaultRdvId = useMemo(() => {
    const hit = rdvMap.get(defaultWorkplace);
    return hit?.rdv_id ?? null;
  }, [rdvMap, defaultWorkplace]);

  const workplaceOptions = useMemo(() => {
    const names = new Set();
    dsRdvs.forEach((r) => {
      const n = String(r.rname ?? "").trim();
      if (n) names.add(n);
    });
    dsSites.forEach((s) => {
      const n = String(s.sname ?? "").trim();
      if (n) names.add(n);
    });
    return [...names];
  }, [dsRdvs, dsSites]);

  const validationCounts = useMemo(() => countValidation(rows), [rows]);

  useEffect(() => {
    const dsid =
      typeof sessionStorage !== "undefined"
        ? (sessionStorage.ondsid || "").trim()
        : "";
    if (!dsid || !apiAddr) return;

    Promise.all([
      axios.get(`${apiAddr}/get/getdsrdvs?dsid=${encodeURIComponent(dsid)}`),
      axios.get(`${apiAddr}/get/dsites?dsid=${encodeURIComponent(dsid)}`),
    ]).then(([rdvRes, siteRes]) => {
      const rdvs = Array.isArray(rdvRes.data) ? rdvRes.data : [];
      const sites = Array.isArray(siteRes.data) ? siteRes.data : [];
      setDsRdvs(rdvs);
      setDsSites(sites);
      if (rdvs[0]?.rname) setDefaultWorkplace(String(rdvRes.data[0].rname).trim());
    });
  }, [apiAddr]);

  const loadExcelFile = useCallback(async (file) => {
    if (!file) return;
    const data = await readXlsxFile(file);
    setUploadedFileName(file.name);
    setRows(parseExcelMatrix(data));
    setValidatedAt(null);
  }, []);

  const runValidation = useCallback(() => {
    setIsValidating(true);
    try {
      const next = validateRows(rows, {
        rdvMap,
        defaultWorkplace,
        defaultRdvId,
      });
      setRows(next);
      setValidatedAt(Date.now());
    } finally {
      setIsValidating(false);
    }
  }, [rows, rdvMap, defaultWorkplace, defaultRdvId]);

  const updateField = useCallback((rowId, fieldKey, value) => {
    setRows((prev) =>
      prev.map((r) =>
        r.id === rowId ? updateRowField(r, fieldKey, value) : r,
      ),
    );
    setValidatedAt(null);
  }, []);

  const deleteRow = useCallback((rowId) => {
    setRows((prev) => reindexRows(prev.filter((r) => r.id !== rowId)));
    setValidatedAt(null);
  }, []);

  const clearRows = useCallback(() => {
    setRows([]);
    setUploadedFileName("");
    setValidatedAt(null);
  }, []);

  const generateRandomRows = useCallback(() => {
    const generated = generateRandomRdvRows({
      count: RANDOM_ROW_DEFAULT_COUNT,
      defaultWorkplace,
      rdvMap,
      defaultRdvId,
    });
    setRows(generated);
    setUploadedFileName("");
    setValidatedAt(null);
  }, [defaultWorkplace, rdvMap, defaultRdvId]);

  const saveRows = useCallback(async () => {
    const dsid =
      typeof sessionStorage !== "undefined"
        ? (sessionStorage.ondsid || "").trim()
        : "";
    if (!dsid || !apiAddr) throw new Error("재난을 선택해주세요.");

    let validated = validateRows(rows, {
      rdvMap,
      defaultWorkplace,
      defaultRdvId,
    });
    setRows(validated);
    const errCount = validated.filter((r) => r.rowStatus === "error").length;
    if (errCount > 0) {
      throw new Error(`오류 ${errCount}건 — 검증 후 다시 시도하세요.`);
    }

    setIsSaving(true);
    setSaveProgress({ done: 0, total: validated.length });
    const results = [...validated];

    for (let i = 0; i < results.length; i += 1) {
      const row = results[i];
      const body =
        row.siteId != null && Number(row.siteId) > 0
          ? {
              dsid,
              site_id: row.siteId,
              person_type: row.personType,
              pname: String(row.pname || "").trim(),
              porg: String(row.porg || "").trim(),
              pdept: String(row.pdept || "").trim(),
              prank: String(row.prank || "").trim(),
              pphone: String(row.pphone || "").trim(),
              duty_desc: String(row.dutyDesc || "").trim(),
            }
          : {
              dsid,
              rdv_id: row.rdvId,
              person_type: row.personType,
              pname: String(row.pname || "").trim(),
              porg: String(row.porg || "").trim(),
              pdept: String(row.pdept || "").trim(),
              prank: String(row.prank || "").trim(),
              pphone: String(row.pphone || "").trim(),
              duty_desc: String(row.dutyDesc || "").trim(),
            };

      try {
        const res = await axios.post(`${apiAddr}/post/rdvcheckin`, body);
        if (res.data?.result === "success") {
          results[i] = {
            ...row,
            saveStatus: "saved",
            rowStatus: "normal",
            attSnum: res.data.snum ?? row.attSnum,
          };
        } else {
          results[i] = {
            ...row,
            saveStatus: "failed",
            fieldMessages: {
              ...row.fieldMessages,
              pname: res.data?.message || "저장 실패",
            },
          };
        }
      } catch (e) {
        results[i] = {
          ...row,
          saveStatus: "failed",
          fieldMessages: {
            ...row.fieldMessages,
            pname: e.response?.data?.message || "저장 오류",
          },
        };
      }
      setSaveProgress({ done: i + 1, total: results.length });
      setRows([...results]);
    }

    socket.emit("rdvattendance", { dsid });
    setIsSaving(false);
    return results;
  }, [rows, rdvMap, defaultWorkplace, defaultRdvId, apiAddr]);

  return {
    rows,
    uploadedFileName,
    validatedAt,
    isValidating,
    isSaving,
    saveProgress,
    validationCounts,
    defaultWorkplace,
    setDefaultWorkplace,
    workplaceOptions,
    loadExcelFile,
    runValidation,
    updateField,
    deleteRow,
    clearRows,
    generateRandomRows,
    saveRows,
    rdvMap,
  };
}
